import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import styled from "styled-components";
import moment from "moment";
import { useRef, useState } from "react";
import { Modal } from "@mui/material";
import { useSnackbar } from "notistack";
import { EmptyItem, Form, SimpleItem } from "devextreme-react/ui/form";
import { Template } from "devextreme-react/core/template";
import DataSource from "devextreme/data/data_source";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import CaseStorageAPI from "../../../api/storage/index.js";
import { usePermissions } from "../../../hooks/usePermissions";
import { getIpAddress, logActivity } from "../../../api/index.js";
import ComboButton from "../../../Monolith-UI/ComboButton/ComboButton.js";
import { LocationsAPI } from "@/Features/Locations/locations.api";
import {
  db_timestamp,
  getDateFormat,
  monolithMoment,
  validateTimestamp,
} from "../../../utils/date-format";
import UserApi from "../../../api/users/users.js";
import UserSelectBoxItem from "../../../components/SelectBoxItems/UserSelectBoxItem";
import {
  SignatureField,
  storageQueryKeys,
  useGetStorageCocRecords,
} from "@/Features";
import ToolBarItems from "../../../components/ToolBarItems.js";
import Loader from "../../../components/Loader.js";
import LocationSelectBoxItem from "../../../components/SelectBoxItems/LocationSelectBoxItem.js";
import { useTheme } from "@monolith-forensics/monolith-ui";
import { DestroyModal } from "../modals/DestroyModal";

const ActionMenu = styled(({ className, storageInfo, records }) => {
  const theme = useTheme();
  const { currentUser, hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [showInsertRecordModal, setShowInsertRecordModal] = useState(false);
  const [showCheckoutItemModal, setShowCheckoutItemModal] = useState(false);
  const [showIntakeModal, setShowIntakeModal] = useState(false);
  const [showReCollectModal, setShowReCollectModal] = useState(false);
  const [showDestroyModal, setShowDestroyModal] = useState(false);

  const destroyedLocationId = LocationsAPI.metaLocations.destroyed.location_id;

  const actions = [
    {
      title: "Move Item",
      onClick: () => setShowMoveModal(true),
      // Moving is how an item leaves the Destroyed location, but only after
      // its Destroy record is deleted (COC_DELETE). While the record stands,
      // a plain Move would put the item back in circulation with its
      // destruction still on the books — and a later bulk destroy would
      // complete silently via the API's stranded-item path, unattested.
      visible:
        storageInfo.location_id !== destroyedLocationId ||
        !records.some((r) => r.type === "Destroy"),
    },
    {
      title: "Checkout Item",
      onClick: () => setShowCheckoutItemModal(true),
      visible:
        records.length !== 0 &&
        storageInfo.location_id !== 1 &&
        storageInfo.location_id !== destroyedLocationId &&
        storageInfo.location_id !== currentUser.location_id,
    },
    {
      title: "Release Item",
      onClick: () => setShowReleaseModal(true),
      visible:
        records.length !== 0 &&
        storageInfo.location_id !== 1 &&
        storageInfo.location_id !== destroyedLocationId,
    },
    {
      title: "Destroy Item",
      onClick: () => setShowDestroyModal(true),
      visible:
        records.length !== 0 &&
        storageInfo.location_id !== 1 &&
        storageInfo.location_id !== destroyedLocationId,
    },
    {
      title: "Re-collect Item",
      onClick: () => setShowReCollectModal(true),
      visible: records.length !== 0 && storageInfo.location_id === 1,
    },
    {
      title: "Insert Record",
      onClick: () => setShowInsertRecordModal(true),
      visible: records.length !== 0 && currentUser.role_id === 1,
    },
  ];

  const reportActions = [
    {
      index: 1,
      title: "Intake Form",
      onClick: () => {
        CaseStorageAPI.createStorageIntakePDF({
          storage: {
            ...storageInfo,
            currenUserFullName: currentUser.full_name,
          },
        }).then(() => {
          logActivity(
            storageInfo.case_id,
            currentUser.user_id,
            `Generated PDF Intake Form for storage item ${storageInfo.storage_number}.`
          );
        });
      },
    },
    {
      index: 2,
      title: "Release Form",
      onClick: () => {
        CaseStorageAPI.createStorageReleasePDF({
          storage: {
            ...storageInfo,
            currenUserFullName: currentUser.full_name,
          },
        }).then(() => {
          logActivity(
            storageInfo.case_id,
            currentUser.user_id,
            `Generated PDF Release Form for storage item ${storageInfo.storage_number}.`
          );
        });
      },
    },
    {
      index: 3,
      title: "Storage COC Report",
      onClick: () => {
        CaseStorageAPI.createStorageCocPDF({
          storage: {
            ...storageInfo,
            currenUserFullName: currentUser.full_name,
          },
          cocData: records,
          options: { suppCoc: false },
        }).then(() => {
          logActivity(
            storageInfo.case_id,
            currentUser.user_id,
            `Generated PDF COC Report for storage item ${storageInfo.storage_number}.`
          );
        });
      },
    },
    {
      index: 4,
      title: "Supplemental COC",
      onClick: () => {
        CaseStorageAPI.createStorageCocPDF({
          storage: {
            ...storageInfo,
            currenUserFullName: currentUser.full_name,
          },
          cocData: records,
          options: { suppCoc: true },
        }).then(() => {
          logActivity(
            storageInfo.case_id,
            currentUser.user_id,
            `Generated PDF COC Report for storage item ${storageInfo.storage_number}.`
          );
        });
      },
    },
  ];

  const handleAction = (action) => {
    action.onClick();
  };

  const refetchCocData = () => {
    // invalidate, not refetch: the Current Location tooltip observers on this
    // key are disabled until hovered, and refetchQueries skips disabled
    // queries — invalidate marks them so the next hover refetches.
    queryClient.invalidateQueries({
      queryKey: storageQueryKeys.cocRecords(storageInfo.storage_id),
    });

    // The overview table now lives under the storageQueryKeys namespace; the
    // legacy "storage:list" prefix still covers StorageSection (case route),
    // the selectors, and the details page. Refresh both.
    queryClient.refetchQueries({
      queryKey: storageQueryKeys.legacyLists,
    });
    queryClient.refetchQueries({
      queryKey: storageQueryKeys.infiniteLists,
    });
  };

  const onMove = () => refetchCocData();
  const onRelease = () => refetchCocData();
  const onInsertRecord = () => refetchCocData();
  const onCheckoutItem = () => refetchCocData();
  const onIntake = () => refetchCocData();
  const onReCollect = () => refetchCocData();
  const onDestroy = () => refetchCocData();

  return (
    <div className={className}>
      {hasPermission() && (
        <ComboButton
          type="dropDown"
          data={actions.filter((a) => a.visible)}
          title={"Actions"}
          showDropdownIcon={true}
          displayField="title"
          variant="outlined"
          useSelectMode={false}
          onItemSelect={handleAction}
          textColor={theme.palette.text.primary}
        >
          Actions
        </ComboButton>
      )}
      <ComboButton
        type="dropDown"
        data={reportActions}
        title={"Report Actions"}
        showDropdownIcon={true}
        displayField="title"
        variant="outlined"
        useSelectMode={false}
        onItemSelect={handleAction}
        textColor={theme.palette.text.primary}
      >
        Reports
      </ComboButton>
      <MoveModal
        open={showMoveModal}
        handleClose={() => setShowMoveModal(false)}
        onSubmit={onMove}
        defaultInfo={storageInfo}
      />
      <ReleaseModal
        open={showReleaseModal}
        handleClose={() => setShowReleaseModal(false)}
        onSubmit={onRelease}
        defaultInfo={storageInfo}
      />
      <InsertRecordModal
        open={showInsertRecordModal}
        handleClose={() => setShowInsertRecordModal(false)}
        onSubmit={onInsertRecord}
        defaultInfo={storageInfo}
      />
      <CheckoutItemModal
        open={showCheckoutItemModal}
        handleClose={() => setShowCheckoutItemModal(false)}
        onSubmit={onCheckoutItem}
        defaultInfo={storageInfo}
      />
      <IntakeItemModal
        open={showIntakeModal}
        handleClose={() => setShowIntakeModal(false)}
        onSubmit={onIntake}
        defaultInfo={storageInfo}
      />
      <ReCollectModal
        open={showReCollectModal}
        handleClose={() => setShowReCollectModal(false)}
        onSubmit={onReCollect}
        defaultInfo={storageInfo}
      />
      <DestroyModal
        open={showDestroyModal}
        handleClose={() => setShowDestroyModal(false)}
        onSubmit={onDestroy}
        defaultInfo={storageInfo}
      />
    </div>
  );
})`
  display: flex;
  flex-direction: row;
  gap: 5px;
  justify-content: space-between;
`;

const MoveModal = ({
  open,
  handleClose = () => {},
  onSubmit = () => {},
  defaultInfo,
}) => {
  const form = useRef(null);
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const isAssigned = defaultInfo.is_assigned === "Yes";

  const receivedFromSig = useRef(null);

  const result = useQueries({
    queries: [
      {
        queryKey: ["evidence:locations", { include_groups: false }],
        queryFn: () => LocationsAPI.get({ include_groups: false }),
        enabled: open,
      },
      {
        queryKey: ["storage:cocMoveReasons"],
        queryFn: () => CaseStorageAPI.getStorageCocMoveReasons(),
        enabled: open,
      },
    ],
  });

  const handleSubmit = async (event) => {
    if (form.current.instance.validate().isValid) {
      let data = { ...form.current.instance.option().formData };

      handleClose();

      if (receivedFromSig.current) {
        const ipAddress = await getIpAddress();

        const signature = {};

        signature.custody_from_sig = receivedFromSig?.current?.isEmpty()
          ? null
          : {
              signature: receivedFromSig.current.getSignature(),
              signer: currentUser.full_name,
              timestamp: moment().toISOString(),
              userAgent: window.navigator.userAgent,
              ip_address: ipAddress,
              monolith_user: {
                first_name: currentUser.first_name,
                last_name: currentUser.last_name,
                full_name: currentUser.full_name,
                email: currentUser.email,
                title: currentUser.title,
                user_id: currentUser.user_id,
              },
              esig: receivedFromSig.current.isESignature,
            };

        data.signature = JSON.stringify(signature);
      }

      const requestObj = {
        caseInfo: {
          case_id: defaultInfo.case_id,
          case_number: defaultInfo.case_number,
          case_ref: defaultInfo.case_ref,
        },
        storageInfo: {
          storage_id: defaultInfo.storage_id,
          uuid: defaultInfo.uuid,
          storage_number: defaultInfo.storage_number,
          is_assigned: defaultInfo.is_assigned,
          case_assignment_id: defaultInfo.case_assignment_id,
        },
        moveData: {
          entered_by_id: currentUser.user_id,
          entered_by: currentUser.full_name,
          new_location_id: data.new_location.location_id,
          new_location_name: data.new_location.location_name,
          current_location_name: defaultInfo.location_name,
          reason: data.reason || "Storage item moved.",
          timestamp: db_timestamp(data.timestamp, true),
          signature: data.signature,
          coc_type: "Move",
        },
      };

      await CaseStorageAPI.moveStorage(requestObj);

      onSubmit(requestObj);

      enqueueSnackbar(
        `Storage Item Moved to ${data.new_location.location_name}`,
        {
          variant: "success",
        }
      );
    }
  };

  const isDone = result.every((r) => r.isFetched);

  const locations = isDone ? result[0].data : [];
  const reasons = isDone ? result[1].data : [];

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") handleClose();
      }}
      style={{ zIndex: 1400 }}
    >
      <div
        style={{
          marginTop: 20,
          marginBottom: 20,
          width: 500,
          height: "auto",
          backgroundColor: theme.palette.background.default,
          position: "fixed",
          left: "calc(50% - 250px)",
          maxHeight: "calc(100vh - 100px)",
          overflowY: "auto",
          padding: 20,
          outline: "none",
        }}
      >
        <div style={{ marginBottom: 15, fontSize: "large" }}>
          Move Storage Item
        </div>

        <Form ref={form} colCount={1}>
          <SimpleItem
            dataField="new_location"
            label={{ text: "New Location" }}
            editorType="dxSelectBox"
            isRequired={true}
            editorOptions={{
              hint: "Select the new location for this storage item.",
              searchEnabled: true,
              dataSource: new DataSource({
                store: locations,
                key: "location_id",
                group: "office_name",
              }),
              displayExpr: "location_name",
              grouped: true,
              dropDownOptions: {
                height: "350px",
              },
              itemTemplate: "new_location",
            }}
          />
          <Template
            name="new_location"
            render={(data) => <LocationSelectBoxItem data={data} />}
          />
          <SimpleItem
            dataField="reason"
            label={{ text: "Move Reason" }}
            editorType="dxSelectBox"
            isRequired={true}
            editorOptions={{
              dataSource: reasons,
              dropDownOptions: {
                maxHeight: 250,
              },
              placeholder: "Select or add new...",
              searchEnabled: true,
              searchExpr: "reason",
              displayExpr: "reason",
              valueExpr: "reason",
              acceptCustomValue: true,
              onCustomItemCreating: (e) => {
                e.customItem = { reason: e.text };
                // Adds the entry to the data source
                e.component.getDataSource().store().insert(e.customItem);
                // Reloads the data source
                e.component.getDataSource().reload();
              },
            }}
          />
          <SimpleItem
            dataField="timestamp"
            label={{ text: "Timestamp" }}
            editorType="dxDateBox"
            isRequired={true}
            visible={isAssigned}
            editorOptions={{
              type: "datetime",
              useMaskBehavior: true,
              displayFormat: getDateFormat({
                isMoment: false,
                includeTime: true,
              }),
              value: new Date(),
            }}
            validationRules={[
              {
                type: "custom",
                message: "Invalid Date",
                validationCallback: validateTimestamp,
              },
            ]}
          />
          <SimpleItem
            dataField="custody_from_sig"
            label={{ text: "Moved By Signature" }}
            visible={isAssigned}
          >
            <SignatureField
              sigFieldRef={receivedFromSig}
              label="Moved By Signature"
              useESignature={true}
            />
          </SimpleItem>
        </Form>
        <ToolBarItems
          submitText="Move Storage"
          onSubmit={handleSubmit}
          onCancel={() => handleClose()}
          style={{ marginTop: 20 }}
        />
      </div>
    </Modal>
  );
};

const CheckoutItemModal = ({
  open,
  handleClose = () => {},
  onSubmit = () => {},
  defaultInfo,
}) => {
  const form = useRef(null);
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const reasons = [];

  const checkoutToSig = useRef(null);

  const handleSubmit = async (event) => {
    let data = { ...form.current.instance.option().formData };

    const ipAddress = await getIpAddress();

    const signature = {};

    signature.custody_to_sig = checkoutToSig.current.isEmpty()
      ? null
      : {
          signature: checkoutToSig.current.getSignature(),
          signer: currentUser.full_name,
          timestamp: moment().toISOString(),
          userAgent: window.navigator.userAgent,
          ip_address: ipAddress,
          monolith_user: {
            first_name: currentUser.first_name,
            last_name: currentUser.last_name,
            full_name: currentUser.full_name,
            email: currentUser.email,
            title: currentUser.title,
            user_id: currentUser.user_id,
          },
          esig: checkoutToSig.current.isESignature,
        };

    data.signature = JSON.stringify(signature);
    handleClose();

    const requestObj = {
      caseInfo: {
        case_id: defaultInfo.case_id,
        case_number: defaultInfo.case_number,
        case_ref: defaultInfo.case_ref,
      },
      storageInfo: {
        storage_id: defaultInfo.storage_id,
        uuid: defaultInfo.uuid,
        storage_number: defaultInfo.storage_number,
        is_assigned: defaultInfo.is_assigned,
        case_assignment_id: defaultInfo.case_assignment_id,
      },
      moveData: {
        entered_by_id: currentUser.user_id,
        entered_by: currentUser.full_name,
        new_location_id: currentUser.location_id,
        new_location_name: currentUser.full_name,
        current_location_name: defaultInfo.location_name,
        reason: data.reason || "Storage checked out by user",
        timestamp: db_timestamp(data.timestamp, true),
        signature: data.signature,
        coc_type: "Checkout",
      },
    };

    await CaseStorageAPI.moveStorage(requestObj);

    onSubmit(requestObj);

    enqueueSnackbar(`Storage Item Checked Out`, {
      variant: "success",
    });
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") handleClose();
      }}
      style={{ zIndex: 1400 }}
    >
      <div
        style={{
          marginTop: 20,
          marginBottom: 20,
          width: 500,
          minHeight: 350,
          backgroundColor: theme.palette.background.default,
          position: "fixed",
          left: "calc(50% - 250px)",
          maxHeight: "calc(100vh - 100px)",
          overflowY: "auto",
          padding: 20,
          outline: "none",
        }}
      >
        <div style={{ marginBottom: 15, fontSize: "large" }}>
          Checkout Storage Item
        </div>
        {!reasons && <Loader />}
        {reasons && (
          <>
            <Form ref={form} colCount={1}>
              <SimpleItem
                dataField="timestamp"
                label={{ text: "Timestamp" }}
                editorType="dxDateBox"
                isRequired={true}
                editorOptions={{
                  type: "datetime",
                  useMaskBehavior: true,
                  displayFormat: getDateFormat({
                    isMoment: false,
                    includeTime: true,
                  }),
                  value: new Date(),
                }}
                validationRules={[
                  {
                    type: "custom",
                    message: "Invalid Date",
                    validationCallback: validateTimestamp,
                  },
                ]}
              />
              <SimpleItem
                dataField="reason"
                label={{ text: "Checkout Notes" }}
                editorType={"dxTextArea"}
                editorOptions={{
                  onContentReady: (e) => {
                    e.component.focus();
                  },
                }}
              />
              <SimpleItem
                dataField="custody_to_sig"
                label={{ text: "Checkout Signature" }}
              >
                <SignatureField
                  sigFieldRef={checkoutToSig}
                  label="Checked Out By Signature"
                  useESignature={true}
                />
              </SimpleItem>
            </Form>
            <ToolBarItems
              submitText="Checkout Storage"
              onSubmit={handleSubmit}
              onCancel={() => handleClose()}
            />
          </>
        )}
      </div>
    </Modal>
  );
};

const ReleaseModal = ({
  open,
  handleClose = () => {},
  onSubmit = () => {},
  defaultInfo,
}) => {
  const form = useRef(null);
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const releasedToSig = useRef(null);
  const releasedBySig = useRef(null);

  const { data: users } = useQuery({
    enabled: open,
    queryKey: [
      "users",
      "list",
      { includeObservers: false, includeInactive: false },
    ],
    queryFn: () =>
      UserApi.getUsers({ includeObservers: false, includeInactive: false }),
  });

  const handleSubmit = async (event) => {
    if (form.current.instance.validate().isValid) {
      const data = { ...form.current.instance.option().formData };

      handleClose();

      const ipAddress = await getIpAddress();

      const signature = {};

      signature.custody_to_sig = releasedToSig.current.isEmpty()
        ? null
        : {
            signature: releasedToSig.current.getSignature(),
            signer: data.released_to,
            timestamp: moment().toISOString(),
            userAgent: window.navigator.userAgent,
            ip_address: ipAddress,
            monolith_user: {
              first_name: currentUser.first_name,
              last_name: currentUser.last_name,
              full_name: currentUser.full_name,
              email: currentUser.email,
              title: currentUser.title,
              user_id: currentUser.user_id,
            },
          };

      signature.custody_from_sig = releasedBySig.current.isEmpty()
        ? null
        : {
            signature: releasedBySig.current.getSignature(),
            signer: data.released_by.full_name,
            timestamp: moment().toISOString(),
            userAgent: window.navigator.userAgent,
            ip_address: ipAddress,
            monolith_user: {
              first_name: currentUser.first_name,
              last_name: currentUser.last_name,
              full_name: currentUser.full_name,
              email: currentUser.email,
              title: currentUser.title,
              user_id: currentUser.user_id,
            },
            esig: releasedBySig.current.isESignature,
          };

      data.signature = JSON.stringify(signature);

      const requestObj = {
        caseInfo: {
          case_id: defaultInfo.case_id,
          case_number: defaultInfo.case_number,
          case_ref: defaultInfo.case_ref,
        },
        storageInfo: {
          storage_id: defaultInfo.storage_id,
          uuid: defaultInfo.uuid,
          storage_number: defaultInfo.storage_number,
          is_assigned: defaultInfo.is_assigned,
          case_assignment_id: defaultInfo.case_assignment_id,
        },
        moveData: {
          entered_by_id: currentUser.user_id,
          entered_by: currentUser.full_name,
          new_location_id: 1,
          new_location_name: data.released_to,
          current_location_name: data.released_by.full_name,
          reason: data.reason || "Storage Released",
          timestamp: db_timestamp(data.timestamp, true),
          signature: data.signature,
          coc_type: "Release",
        },
      };

      await CaseStorageAPI.moveStorage(requestObj);

      onSubmit(requestObj);

      enqueueSnackbar(`Storage Item Released to ${data.released_to}`, {
        variant: "success",
      });
    }
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") handleClose();
      }}
      style={{ zIndex: 1400 }}
    >
      <div
        style={{
          marginTop: 20,
          marginBottom: 20,
          width: 500,
          minHeight: 350,
          backgroundColor: theme.palette.background.default,
          position: "fixed",
          left: "calc(50% - 250px)",
          maxHeight: "calc(100vh - 100px)",
          overflowY: "auto",
          padding: 20,
          outline: "none",
        }}
      >
        <div style={{ marginBottom: 15, fontSize: "large" }}>
          Release Storage Item
        </div>
        {users && (
          <>
            <Form ref={form} colCount={1}>
              <SimpleItem
                dataField="released_by"
                label={{ text: "Released By" }}
                editorType="dxSelectBox"
                isRequired={true}
                editorOptions={{
                  dataSource: users,
                  searchEnabled: true,
                  displayExpr: "full_name",
                  dropDownOptions: {
                    maxHeight: 250,
                  },
                  itemTemplate: "released_by",
                }}
              />
              <Template
                name="released_by"
                render={(data) => <UserSelectBoxItem data={data} />}
              />
              <SimpleItem
                dataField="released_to"
                label={{ text: "Released To" }}
                isRequired={true}
                editorOptions={{
                  placeholder: "Enter Name...",
                }}
              />
              <SimpleItem
                dataField="timestamp"
                label={{ text: "Timestamp" }}
                editorType="dxDateBox"
                isRequired={true}
                editorOptions={{
                  type: "datetime",
                  useMaskBehavior: true,
                  displayFormat: getDateFormat({
                    isMoment: false,
                    includeTime: true,
                  }),
                  value: new Date(),
                }}
                validationRules={[
                  {
                    type: "custom",
                    message: "Invalid Date",
                    validationCallback: validateTimestamp,
                  },
                ]}
              />
              <SimpleItem
                dataField="reason"
                label={{ text: "Release Notes" }}
                editorType={"dxTextArea"}
              />
              <SimpleItem
                dataField="custody_from_sig"
                label={{ text: "Released By Signature" }}
              >
                <SignatureField
                  sigFieldRef={releasedBySig}
                  label="Released By Signature"
                  useESignature={true}
                />
              </SimpleItem>
              <SimpleItem
                dataField="custody_to_sig"
                label={{ text: "Released To Signature" }}
              >
                <SignatureField
                  sigFieldRef={releasedToSig}
                  label="Released To Signature"
                />
              </SimpleItem>
            </Form>
            <ToolBarItems
              submitText="Release Storage"
              onSubmit={handleSubmit}
              onCancel={() => handleClose()}
            />
          </>
        )}
        {!users && <Loader />}
      </div>
    </Modal>
  );
};

const ReCollectModal = ({
  open,
  handleClose = () => {},
  onSubmit = () => {},
  defaultInfo,
}) => {
  const form = useRef(null);
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const receivedBySig = useRef(null);
  const receivedFromSig = useRef(null);

  const { data: users } = useQuery({
    enabled: open,
    queryKey: [
      "users",
      "list",
      { includeObservers: false, includeInactive: false },
    ],
    queryFn: () =>
      UserApi.getUsers({ includeObservers: false, includeInactive: false }),
  });

  const handleSubmit = async (event) => {
    if (form.current.instance.validate().isValid) {
      let data = { ...form.current.instance.option().formData };

      const ipAddress = await getIpAddress();

      const signature = {};

      signature.custody_to_sig = receivedBySig.current.isEmpty()
        ? null
        : {
            signature: receivedBySig.current.getSignature(),
            signer: data.received_by.full_name,
            timestamp: moment().toISOString(),
            userAgent: window.navigator.userAgent,
            ip_address: ipAddress,
            monolith_user: {
              first_name: currentUser.first_name,
              last_name: currentUser.last_name,
              full_name: currentUser.full_name,
              email: currentUser.email,
              title: currentUser.title,
              user_id: currentUser.user_id,
            },
            esig: receivedBySig.current.isESignature,
          };

      signature.custody_from_sig = receivedFromSig.current.isEmpty()
        ? null
        : {
            signature: receivedFromSig.current.getSignature(),
            signer: data.received_from,
            timestamp: moment().toISOString(),
            userAgent: window.navigator.userAgent,
            ip_address: ipAddress,
            monolith_user: {
              first_name: currentUser.first_name,
              last_name: currentUser.last_name,
              full_name: currentUser.full_name,
              email: currentUser.email,
              title: currentUser.title,
              user_id: currentUser.user_id,
            },
          };

      data.signature = JSON.stringify(signature);

      handleClose();

      const requestObj = {
        caseInfo: {
          case_id: defaultInfo.case_id,
          case_number: defaultInfo.case_number,
          case_ref: defaultInfo.case_ref,
        },
        storageInfo: {
          storage_id: defaultInfo.storage_id,
          uuid: defaultInfo.uuid,
          storage_number: defaultInfo.storage_number,
          is_assigned: defaultInfo.is_assigned,
          case_assignment_id: defaultInfo.case_assignment_id,
        },
        moveData: {
          entered_by_id: currentUser.user_id,
          entered_by: currentUser.full_name,
          new_location_id: data.received_by.location_id,
          new_location_name: data.received_by.full_name,
          current_location_name: data.received_from,
          reason: data.reason || "Storage Re-Collected",
          timestamp: db_timestamp(data.timestamp, true),
          signature: data.signature,
          coc_type: "Re-Collect",
        },
      };

      await CaseStorageAPI.moveStorage(requestObj);

      onSubmit(requestObj);

      enqueueSnackbar(`Storage Item Re-collected from ${data.received_from}`, {
        variant: "success",
      });
    }
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") handleClose();
      }}
      style={{ zIndex: 1400 }}
    >
      <div
        style={{
          marginTop: 20,
          marginBottom: 20,
          width: 500,
          minHeight: 450,
          backgroundColor: theme.palette.background.default,
          position: "fixed",
          left: "calc(50% - 250px)",
          maxHeight: "calc(100vh - 100px)",
          overflowY: "auto",
          padding: 20,
          outline: "none",
        }}
      >
        <div style={{ marginBottom: 15, fontSize: "large" }}>
          Recollect Storage Item
        </div>
        {users && (
          <>
            <Form ref={form} colCount={1}>
              <SimpleItem
                dataField="received_by"
                label={{ text: "Received By" }}
                editorType="dxSelectBox"
                isRequired={true}
                editorOptions={{
                  dataSource: users,
                  searchEnabled: true,
                  displayExpr: "full_name",
                  dropDownOptions: {
                    maxHeight: 250,
                  },
                  itemTemplate: "received_by",
                }}
              />
              <Template
                name="received_by"
                render={(data) => <UserSelectBoxItem data={data} />}
              />
              <SimpleItem
                dataField="received_from"
                label={{ text: "Received From" }}
                isRequired={true}
                editorOptions={{
                  placeholder: "Enter Name...",
                }}
              />
              <SimpleItem
                dataField="timestamp"
                label={{ text: "Timestamp" }}
                editorType="dxDateBox"
                isRequired={true}
                editorOptions={{
                  type: "datetime",
                  useMaskBehavior: true,
                  displayFormat: getDateFormat({
                    isMoment: false,
                    includeTime: true,
                  }),
                  value: new Date(),
                }}
                validationRules={[
                  {
                    type: "custom",
                    message: "Invalid Date",
                    validationCallback: validateTimestamp,
                  },
                ]}
              />
              <SimpleItem
                dataField="reason"
                label={{ text: "Re-Collection Notes" }}
                editorType={"dxTextArea"}
              />
              <SimpleItem
                dataField="custody_to_sig"
                label={{ text: "Received By Signature" }}
              >
                <SignatureField
                  sigFieldRef={receivedBySig}
                  label="Received By Signature"
                  useESignature={true}
                />
              </SimpleItem>
              <SimpleItem
                dataField="custody_from_sig"
                label={{ text: "Received From Signature" }}
              >
                <SignatureField
                  sigFieldRef={receivedFromSig}
                  label="Received From Signature"
                />
              </SimpleItem>
            </Form>
            <ToolBarItems
              submitText="Recollect Storage"
              onSubmit={handleSubmit}
              onCancel={() => handleClose()}
            />
          </>
        )}
        {!users && <Loader />}
      </div>
    </Modal>
  );
};

const InsertRecordModal = ({
  open,
  handleClose = () => {},
  onSubmit = () => {},
  defaultInfo,
}) => {
  const form = useRef(null);
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const custodyToSig = useRef(null);
  const custodyFromSig = useRef(null);

  const handleSubmit = async (event) => {
    if (form.current.instance.validate().isValid) {
      let data = { ...form.current.instance.option().formData };

      handleClose();

      const ipAddress = await getIpAddress();

      const signature = {};

      signature.custody_to_sig = custodyToSig.current.isEmpty()
        ? null
        : {
            signature: custodyToSig.current.getSignature(),
            signer: data.custody_to,
            timestamp: moment().toISOString(),
            userAgent: window.navigator.userAgent,
            ip_address: ipAddress,
            monolith_user: {
              first_name: currentUser.first_name,
              last_name: currentUser.last_name,
              full_name: currentUser.full_name,
              email: currentUser.email,
              title: currentUser.title,
              user_id: currentUser.user_id,
            },
            esig: custodyToSig.current.isESignature,
          };

      signature.custody_from_sig = custodyFromSig.current.isEmpty()
        ? null
        : {
            signature: custodyFromSig.current.getSignature(),
            signer: data.custody_from,
            timestamp: moment().toISOString(),
            userAgent: window.navigator.userAgent,
            ip_address: ipAddress,
            monolith_user: {
              first_name: currentUser.first_name,
              last_name: currentUser.last_name,
              full_name: currentUser.full_name,
              email: currentUser.email,
              title: currentUser.title,
              user_id: currentUser.user_id,
            },
            esig: custodyFromSig.current.isESignature,
          };

      data.signature = JSON.stringify(signature);

      await CaseStorageAPI.createCocRecord({
        storage_id: defaultInfo.storage_id,
        custody_to: data.custody_to,
        custody_from: data.custody_from,
        reason: data.reason,
        entered_by: currentUser.full_name,
        timestamp: db_timestamp(data.timestamp, true),
        signature: data.signature,
        coc_type: data.coc_type || null,
        case_assignment_id: defaultInfo.case_assignment_id,
      });

      onSubmit({
        custody_to: data.custody_to,
        custody_from: data.custody_from,
        reason: data.reason,
        entered_by: currentUser.full_name,
        timestamp: db_timestamp(data.timestamp, true),
        signature: data.signature,
        coc_type: data.coc_type || null,
      });

      enqueueSnackbar(`Chain of Custody Record Inserted`, {
        variant: "success",
      });
    }
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") handleClose();
      }}
      style={{ zIndex: 1400 }}
    >
      <div
        style={{
          marginTop: 20,
          marginBottom: 20,
          width: 500,
          minHeight: 450,
          backgroundColor: theme.palette.background.default,
          position: "fixed",
          left: "calc(50% - 250px)",
          maxHeight: "calc(100vh - 100px)",
          overflowY: "auto",
          padding: 20,
          outline: "none",
        }}
      >
        <div style={{ marginBottom: 15, fontSize: "large" }}>
          Create Chain of Custody Record
        </div>
        <Form ref={form} colCount={1}>
          <SimpleItem
            dataField="custody_from"
            label={{ text: "Custody From" }}
            isRequired={true}
          />
          <SimpleItem
            dataField="custody_to"
            label={{ text: "Custody To" }}
            isRequired={true}
          />
          <SimpleItem
            dataField="timestamp"
            label={{ text: "Timestamp" }}
            editorType="dxDateBox"
            isRequired={true}
            editorOptions={{
              type: "datetime",
              useMaskBehavior: true,
              displayFormat: getDateFormat({
                isMoment: false,
                includeTime: true,
              }),
              value: new Date(),
            }}
            validationRules={[
              {
                type: "custom",
                message: "Invalid Date",
                validationCallback: validateTimestamp,
              },
            ]}
          />
          <SimpleItem
            dataField="coc_type"
            label={{ text: "COC Type" }}
            isRequired={true}
            editorType="dxSelectBox"
            editorOptions={{
              items: ["Move", "Checkout", "Release", "Re-Collect", "Destroy"],
            }}
          />
          <SimpleItem
            dataField="reason"
            label={{ text: "COC Notes" }}
            isRequired={true}
            editorType="dxTextArea"
          />
          <SimpleItem
            dataField="custody_from_sig"
            label={{ text: "Custody From Signature" }}
          >
            <SignatureField
              sigFieldRef={custodyFromSig}
              label="Custody From Signature"
              useESignature={true}
            />
          </SimpleItem>
          <SimpleItem
            dataField="custody_to_sig"
            label={{ text: "Custody To Signature" }}
          >
            <SignatureField
              sigFieldRef={custodyToSig}
              label="Custody To Signature"
              useESignature={true}
            />
          </SimpleItem>
        </Form>
        <ToolBarItems
          submitText="Create Record"
          onSubmit={handleSubmit}
          onCancel={() => handleClose()}
        />
      </div>
    </Modal>
  );
};

const IntakeItemModal = ({
  open,
  handleClose = () => {},
  onSubmit = () => {},
  defaultInfo,
}) => {
  const form = useRef(null);
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const receivedBySig = useRef(null);
  const receivedFromSig = useRef(null);

  const handleSubmit = async (event) => {
    if (form.current.instance.validate().isValid) {
      let formData = Object.assign(
        {},
        form.current.instance.option("formData")
      );

      const ipAddress = await getIpAddress();

      const signature = {};

      signature.custody_to_sig = receivedBySig.current.isEmpty()
        ? null
        : {
            signature: receivedBySig.current.getSignature(),
            signer: formData.custody_to.location_name,
            timestamp: moment().toISOString(),
            userAgent: window.navigator.userAgent,
            ip_address: ipAddress,
            monolith_user: {
              first_name: currentUser.first_name,
              last_name: currentUser.last_name,
              full_name: currentUser.full_name,
              email: currentUser.email,
              title: currentUser.title,
              user_id: currentUser.user_id,
            },
            esig: receivedBySig.current.isESignature,
          };

      signature.custody_from_sig = receivedFromSig.current.isEmpty()
        ? null
        : {
            signature: receivedFromSig.current.getSignature(),
            signer: formData.custody_from,
            timestamp: moment().toISOString(),
            userAgent: window.navigator.userAgent,
            ip_address: ipAddress,
            monolith_user: {
              first_name: currentUser.first_name,
              last_name: currentUser.last_name,
              full_name: currentUser.full_name,
              email: currentUser.email,
              title: currentUser.title,
              user_id: currentUser.user_id,
            },
          };

      handleClose();
      //Convert intake time to UTC
      if (formData.coc_intake_timestamp) {
        formData.coc_intake_timestamp = db_timestamp(
          formData.coc_intake_timestamp,
          true
        );

        formData.signature = JSON.stringify(signature);

        formData.entered_by = currentUser.full_name;
      }

      formData.storage_id = defaultInfo.storage_id;
      formData.custody_to = formData.custody_to.location_id;
      formData.coc_type = "Intake";
      formData.reason = formData.reason ? formData.reason : "Storage Intake";

      CaseStorageAPI.createStorageIntakeDetails(formData).then((result) => {
        onSubmit();

        enqueueSnackbar(`Entered Storage Intake Details`, {
          variant: "success",
        });

        logActivity(
          defaultInfo.case_id,
          currentUser.user_id,
          `Entered Storage Intake Details: ${defaultInfo.storage_number}`
        );
      });
    } else {
      enqueueSnackbar(`Please complete all required fields.`, {
        variant: "error",
      });
    }
  };

  const { data: locations } = useQuery({
    queryKey: ["evidence:locations", { include_groups: false }],
    queryFn: () => LocationsAPI.get({ include_groups: false }),
    enabled: open,
  });

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") handleClose();
      }}
      style={{ zIndex: 1400 }}
    >
      <div
        style={{
          marginTop: 20,
          marginBottom: 20,
          width: 800,
          minHeight: 500,
          backgroundColor: theme.palette.background.default,
          position: "fixed",
          left: "calc(50% - 400px)",
          maxHeight: "calc(100vh - 100px)",
          overflowY: "auto",
          padding: 20,
          outline: "none",
        }}
      >
        <div style={{ marginBottom: 15, fontSize: "large" }}>
          Create Storage Intake Details
        </div>
        {locations ? (
          <>
            <Form ref={form} colCount={2}>
              <SimpleItem
                dataField="custody_to"
                label={{ text: "Received By" }}
                isRequired={true}
                editorType="dxSelectBox"
                editorOptions={{
                  dataSource: locations,
                  hint: "This is the person or location in your org that received the item.",
                  dropDownOptions: {
                    maxHeight: 250,
                  },
                  displayExpr: "location_name",
                  searchEnabled: true,
                  itemTemplate: "custody_to",
                }}
              ></SimpleItem>
              <Template
                name="custody_to"
                render={(data) => <LocationSelectBoxItem data={data} />}
              />
              <SimpleItem
                dataField="custody_from"
                label={{ text: "Received From" }}
                isRequired={true}
                editorOptions={{
                  placeholder: "John Smith, FedEx: tracking 12345...",
                  hint: "This is the person or entity the item was received from.",
                }}
              ></SimpleItem>
              <SimpleItem
                dataField="custody_to_sig"
                label={{ text: "Received By Signature" }}
              >
                <SignatureField
                  sigFieldRef={receivedBySig}
                  label="Received By Signature"
                  useESignature={true}
                />
              </SimpleItem>
              <SimpleItem
                dataField="custody_from_sig"
                label={{ text: "Received From Signature" }}
              >
                <SignatureField
                  sigFieldRef={receivedFromSig}
                  label="Received From Signature"
                />
              </SimpleItem>
              <SimpleItem
                dataField="coc_intake_timestamp"
                label={{ text: "Intake Timestamp" }}
                isRequired={true}
                editorType="dxDateBox"
                editorOptions={{
                  hint: "Date and time the item was received.",
                  type: "datetime",
                  useMaskBehavior: true,
                  displayFormat: getDateFormat({
                    isMoment: false,
                    includeTime: true,
                  }),
                  pickerType: "calendar",
                  value: new Date(),
                }}
              ></SimpleItem>
              <SimpleItem
                dataField="location_received"
                label={{ text: "Location Received" }}
                editorOptions={{
                  placeholder: "Address, Front Office, etc...",
                }}
              />
              <SimpleItem
                dataField="reason"
                label={{ text: "Notes" }}
                colSpan={2}
                editorType="dxTextArea"
                editorOptions={{
                  height: 75,
                }}
              ></SimpleItem>
            </Form>
            <ToolBarItems
              submitText="Create Intake Details"
              onSubmit={handleSubmit}
              onCancel={() => handleClose()}
              style={{ marginTop: 10 }}
            />
          </>
        ) : (
          <Loader />
        )}
      </div>
    </Modal>
  );
};

const CocRecord = styled(({ className, storageInfo, record }) => {
  const [open, setOpen] = useState(false);
  const handleClick = () => setOpen(!open);
  return (
    <div className={className}>
      <div className="outer-container">
        <div>
          <div className="record-type">{record.type || "None"}</div>
          <div className="record-timestamp">
            <div
              style={{ marginRight: 5 }}
              title={monolithMoment({
                timestamp: record.timestamp,
                includeTime: true,
              })}
            >
              {moment.utc(record.timestamp).fromNow()}
            </div>
          </div>
          {!open && (
            <div className="coc-preview-data">
              <div className="coc-data">
                Custody From:{" "}
                <span title={record.custody_from}>{record.custody_from}</span>
              </div>
              <div className="coc-data">
                Custody To:{" "}
                <span title={record.custody_to}>{record.custody_to}</span>
              </div>
              <div className="coc-data">
                Description: <span title={record.reason}>{record.reason}</span>
              </div>
            </div>
          )}
        </div>
        <div className="view-btn" onClick={() => handleClick()}>
          <div className="view-btn-title">View Details</div>
          <ArrowDownwardOutlinedIcon
            style={{
              //rotate
              transform: !open ? "rotate(-90deg)" : "rotate(0deg)",
              transition: "transform 0.3s ease-in-out",
            }}
          />
        </div>
      </div>
      {open && <Details item={record} />}
    </div>
  );
})`
  font-size: 14px;
  padding: 15px;
  border: 1px solid ${(props) => props.theme.palette.divider};
  border-radius: 5px;

  & .outer-container {
    display: flex;
  }

  & .record-type {
    font-size: 16px;
    font-weight: 600;
    margin-right: 10px;
  }

  & .record-timestamp {
    display: flex;
    align-items: center;
    color: ${({ theme }) => theme.palette.text.secondary};
    font-size: 12px;
    :hover {
      color: ${({ theme }) => theme.palette.text.primary};
      cursor: pointer;
    }
  }

  & .coc-preview-data {
    display: flex;
    flex-wrap: wrap;
    color: ${({ theme }) => theme.palette.text.secondary};
    font-size: 11px;
    margin-top: 10px;
  }

  & .coc-data {
    margin-right: 15px;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & .coc-data span {
    color: ${({ theme }) => theme.palette.text.primary};
  }

  & .view-btn {
    cursor: pointer;
    font-size: 10px;
    color: ${({ theme }) => theme.palette.text.secondary};
    display: flex;
    align-items: center;
    margin-left: auto;
    svg {
      font-size: 15px;
    }
    &:hover,
    &:hover svg,
    &:hover .view-btn-title {
      color: ${({ theme }) => theme.palette.primary.main};
      text-decoration: underline;
    }
  }
  & .view-btn-title {
    margin-right: 5px;
  }
`;

const Details = styled(({ className, item }) => {
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModel, setShowDeleteModel] = useState(false);
  const queryClient = useQueryClient();
  const theme = useTheme();

  const onEdit = () => {
    console.log("onEdit");
    // invalidate, not refetch — see refetchCocData in ActionMenu above.
    queryClient.invalidateQueries({
      queryKey: storageQueryKeys.cocRecords(item.storage_id),
    });
  };

  let signature = item?.signature || {};

  return (
    <div className={className}>
      <div style={{ display: "flex", width: "fit-content" }}>
        {hasPermission(MONOLITH_PERMISSIONS.COC_UPDATE) && (
          <div
            className="action-menu-item"
            onClick={() => setShowEditModal(true)}
            style={{ marginBottom: 10 }}
          >
            <EditOutlinedIcon style={{ color: theme.palette.primary.main }} />
            <div className="action-menu-label">Edit</div>
          </div>
        )}
        <EditCocRecordModal
          open={showEditModal}
          handleClose={() => setShowEditModal(false)}
          onSubmit={onEdit}
          defaultInfo={item}
        />
        {hasPermission(MONOLITH_PERMISSIONS.COC_DELETE) && (
          <div
            className="action-menu-item"
            onClick={() => setShowDeleteModel(true)}
            style={{ marginBottom: 10 }}
          >
            <DeleteOutlineOutlinedIcon
              style={{ color: theme.palette.error.main }}
            />
            <div className="action-menu-label">Delete</div>
          </div>
        )}
        <DeleteModal
          open={showDeleteModel}
          handleClose={() => setShowDeleteModel(false)}
          onSubmit={onEdit}
          defaultInfo={item}
        />
      </div>
      <DetailItem label="Type" value={item.type || "None"} />
      <DetailItem
        label="Timestamp"
        value={monolithMoment({
          timestamp: item.timestamp,
          includeTime: true,
          includeZone: true,
        })}
      />
      <DetailItem
        label="Custody From"
        value={
          <div>
            <div>{item.custody_from}</div>
            {signature?.custody_from_sig && (
              <div style={{ marginTop: 20, marginBottom: 20 }}>
                <img
                  src={signature.custody_from_sig.signature}
                  style={{
                    maxHeight: 30,
                    filter: theme.name === "DARK" ? "invert(1)" : "unset",
                  }}
                  alt="signature"
                />
              </div>
            )}
          </div>
        }
      />
      <DetailItem
        label="Custody To"
        value={
          <div>
            <div>{item.custody_to}</div>
            {signature?.custody_to_sig && (
              <div style={{ marginTop: 20, marginBottom: 20 }}>
                <img
                  src={signature.custody_to_sig.signature}
                  style={{
                    maxHeight: 30,
                    filter: theme.name === "DARK" ? "invert(1)" : "unset",
                  }}
                  alt="signature"
                />
              </div>
            )}
          </div>
        }
      />
      <DetailItem label="Description" value={item.reason} />
    </div>
  );
})`
  margin-top: 15px;
`;

export const DetailItem = styled(({ className, label, value }) => {
  return (
    <div className={className}>
      <div className="request-detail-label">{label}</div>
      <div className="request-detail-value">{value}</div>
    </div>
  );
})`
  display: flex;
  margin-bottom: 8px;
  font-size: 12px;
  .request-detail-label {
    color: ${(props) => props.theme.palette.text.secondary};
    margin-right: 10px;
    min-width: 100px;
  }
  .request-detail-value {
    color: ${(props) => props.theme.palette.text.primary};
    white-space: pre-line;
  }
`;

const EditCocRecordModal = ({
  open,
  handleClose = () => {},
  onSubmit = () => {},
  defaultInfo,
}) => {
  const form = useRef(null);
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const custodyToSig = useRef(null);
  const custodyFromSig = useRef(null);

  const handleSubmit = async (event) => {
    if (form.current.instance.validate().isValid) {
      const data = { ...form.current.instance.option().formData };

      handleClose();

      const ipAddress = await getIpAddress();

      const signature = {};

      signature.custody_to_sig = custodyToSig.current.isEmpty()
        ? null
        : {
            signature: custodyToSig.current.getSignature(),
            signer: data.custody_to,
            timestamp: moment().toISOString(),
            userAgent: window.navigator.userAgent,
            ip_address: ipAddress,
            monolith_user: {
              first_name: currentUser.first_name,
              last_name: currentUser.last_name,
              full_name: currentUser.full_name,
              email: currentUser.email,
              title: currentUser.title,
              user_id: currentUser.user_id,
            },
          };

      signature.custody_from_sig = custodyFromSig.current.isEmpty()
        ? null
        : {
            signature: custodyFromSig.current.getSignature(),
            signer: data.custody_from,
            timestamp: moment().toISOString(),
            userAgent: window.navigator.userAgent,
            ip_address: ipAddress,
            monolith_user: {
              first_name: currentUser.first_name,
              last_name: currentUser.last_name,
              full_name: currentUser.full_name,
              email: currentUser.email,
              title: currentUser.title,
              user_id: currentUser.user_id,
            },
            esig: custodyFromSig.current.isESignature,
          };

      data.signature = JSON.stringify(signature);

      await CaseStorageAPI.updateCocRecord(defaultInfo.coc_id, {
        ...data,
        timestamp: db_timestamp(data.timestamp, true),
      });

      onSubmit({
        ...data,
        timestamp: db_timestamp(data.timestamp, true),
      });

      enqueueSnackbar(`Chain of Custody Record Edited`, {
        variant: "success",
      });
    }
  };

  const defaultFormData = {
    custody_from: defaultInfo.custody_from,
    custody_to: defaultInfo.custody_to,
    reason: defaultInfo.reason,
    timestamp: defaultInfo.timestamp,
    type: defaultInfo.type || null,
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") handleClose();
      }}
      style={{ zIndex: 1400 }}
    >
      <div
        style={{
          marginTop: 20,
          marginBottom: 20,
          width: 650,
          minHeight: 450,
          backgroundColor: theme.palette.background.default,
          position: "fixed",
          left: "calc(50% - 325px)",
          maxHeight: "calc(100vh - 100px)",
          overflowY: "auto",
          padding: 20,
          outline: "none",
        }}
      >
        <div style={{ marginBottom: 15, fontSize: "large" }}>
          Edit Chain of Custody Record
        </div>
        <Form ref={form} colCount={2} defaultFormData={{ ...defaultFormData }}>
          <SimpleItem
            dataField="custody_from"
            label={{ text: "Custody From" }}
            isRequired={true}
          />
          <SimpleItem
            dataField="custody_to"
            label={{ text: "Custody To" }}
            isRequired={true}
          />
          <SimpleItem
            dataField="timestamp"
            label={{ text: "Timestamp" }}
            editorType="dxDateBox"
            isRequired={true}
            editorOptions={{
              type: "datetime",
              useMaskBehavior: true,
              displayFormat: getDateFormat({
                isMoment: false,
                includeTime: true,
              }),
            }}
            validationRules={[
              {
                type: "custom",
                message: "Invalid Date",
                validationCallback: validateTimestamp,
              },
            ]}
          />
          {defaultInfo.type !== "Intake" ? (
            <SimpleItem
              dataField="type"
              label={{ text: "COC Type" }}
              isRequired={true}
              editorType="dxSelectBox"
              editorOptions={{
                items: ["Move", "Checkout", "Release", "Re-Collect", "Destroy"],
              }}
            />
          ) : (
            <EmptyItem />
          )}
          <SimpleItem
            dataField="reason"
            label={{ text: "Notes" }}
            colSpan={2}
            isRequired={true}
            editorType="dxTextArea"
            editorOptions={{
              height: 100,
            }}
          />
          <SimpleItem
            dataField="custody_from_sig"
            label={{ text: "Custody From Signature" }}
          >
            <SignatureField
              sigFieldRef={custodyFromSig}
              defaultValue={
                defaultInfo?.signature?.custody_from_sig?.signature || null
              }
              label="Custody From Signature"
              useESignature={true}
            />
          </SimpleItem>
          <SimpleItem
            dataField="custody_to_sig"
            label={{ text: "Custody To Signature" }}
          >
            <SignatureField
              sigFieldRef={custodyToSig}
              defaultValue={
                defaultInfo?.signature?.custody_to_sig?.signature || null
              }
              label="Custody To Signature"
            />
          </SimpleItem>
        </Form>
        <ToolBarItems
          submitText="Edit Record"
          onSubmit={handleSubmit}
          onCancel={() => handleClose()}
        />
      </div>
    </Modal>
  );
};

const DeleteModal = ({
  open,
  handleClose = () => {},
  onSubmit = () => {},
  defaultInfo,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = () => {
    CaseStorageAPI.deleteCocRecord(defaultInfo.coc_id).then(() => {
      enqueueSnackbar(`Chain of Custody Record Deleted.`, {
        variant: "success",
      });
      onSubmit();
    });
    handleClose();
  };

  const handleCancel = () => handleClose();

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") handleClose();
      }}
      style={{ zIndex: 1400 }}
    >
      <div
        style={{
          marginTop: 20,
          marginBottom: 20,
          width: 400,
          backgroundColor: theme.palette.background.default,
          position: "fixed",
          left: "calc(50% - 200px)",
          maxHeight: "calc(100vh - 100px)",
          overflowY: "auto",
          padding: 20,
          outline: "none",
        }}
      >
        <div style={{ marginBottom: 15, fontSize: "large" }}>
          Delete Chain of Custody Record
        </div>
        <div style={{ margin: "10px 0px" }}>
          Are you sure you want to delete this Chain of Custody Record?
        </div>
        <div style={{ margin: "20px 0px" }}>
          <DetailItem label="Type" value={defaultInfo.type} />
          <DetailItem
            label="Timestamp"
            value={monolithMoment({
              timestamp: defaultInfo.timestamp,
              includeTime: true,
              includeZone: true,
            })}
          />
          <DetailItem
            label="Custody From"
            value={
              <div>
                <div>{defaultInfo.custody_from}</div>
              </div>
            }
          />
          <DetailItem
            label="Custody To"
            value={
              <div>
                <div>{defaultInfo.custody_to}</div>
              </div>
            }
          />
          <DetailItem label="Description" value={defaultInfo.reason} />
        </div>
        <div
          style={{
            marginTop: 30,
          }}
        >
          <ToolBarItems
            submitText="Delete Record"
            submitColor="error"
            onSubmit={handleSubmit}
            onCancel={handleCancel}
          />
        </div>
      </div>
    </Modal>
  );
};

const CocContent = styled(({ className, storageInfo }) => {
  const { data } = useGetStorageCocRecords(
    storageInfo.storage_id,
    storageInfo.case_assignment_id
  );

  if (!data) return null;

  data &&
    data.sort((a, b) => {
      if (a.timestamp > b.timestamp) return 1;
      if (a.timestamp < b.timestamp) return -1;
      return 0;
    });

  return (
    <div className={className}>
      <ActionMenu storageInfo={storageInfo} records={data} />
      {storageInfo.is_assigned === "No" && data.length === 0 ? (
        <div style={{ marginTop: 10, marginBottom: 20 }}>
          Moves of unassigned storage items are not tracked in the chain of
          custody. You can still move the item for asset tracking purposes.
        </div>
      ) : data.length === 0 ? (
        <div style={{ marginTop: 10, marginBottom: 20 }}>
          No chain of custody records
        </div>
      ) : (
        <div className="coc-container">
          {data?.map((record) => {
            return (
              <CocRecord
                key={record.coc_id}
                storageInfo={storageInfo}
                record={record}
              />
            );
          })}
        </div>
      )}
    </div>
  );
})`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0px 5px;

  overflow-y: hidden;

  .coc-container {
    display: flex;
    flex-direction: column;
    gap: 10px;

    flex: 1 1 auto;
    overflow-y: auto;
  }
`;

export default CocContent;
